import { Box, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Close";
import { PageHeader } from "../../page-header";
import { SubHeader } from "../../sub-header";
import styles from "./customer-contract-attachments.module.scss";
import {
  PrimaryButton,
  RiverSpinner,
  useNotification,
  useSimpleDialog,
} from "@river/common-ui";
import { ContractContext } from "../contract.context";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { IContractAttachment } from "@river/interfaces";
import { customerService } from "../../../services";
import { CustomerContext } from "../../customer/customer.context";
import dayjs from "dayjs";

export const CustomerContractAttachments = () => {
  const { customer } = useContext(CustomerContext)!;
  const { contract, fetchContract } = useContext(ContractContext)!;
  const notify = useNotification();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<IContractAttachment[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] =
    useState<IContractAttachment | null>(null);
  const [loadingAttachments, setLoadingAttachments] = useState<boolean>(false);
  const [downloadingAttachment, setDownloadingAttachment] =
    useState<boolean>(false);
  const [deletingAttachment, setDeletingAttachment] = useState<boolean>(false);
  const [uploadingAttachment, setUploadingAttachment] =
    useState<boolean>(false);

  const fetchAttachments = async () => {
    if (!contract?._id || !customer?._id) return;
    setLoadingAttachments(true);
    try {
      const data = await customerService.getContractAttachments(
        customer._id,
        contract._id
      );
      setAttachments(data);
    } catch (message) {
      console.error({ message });
    } finally {
      setLoadingAttachments(false);
    }
  };

  async function downloadAttachment(attachment: IContractAttachment) {
    if (!customer?._id || !contract?._id) {
      return;
    }
    setDownloadingAttachment(true);
    try {
      const { url } = await customerService.getContractAttachmentDownloadUrl(
        customer._id,
        contract._id,
        attachment._id
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = attachment.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (message) {
      notify.error({ message });
    } finally {
      setDownloadingAttachment(false);
    }
  }

  async function onFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !customer?._id || !contract?._id) {
      return;
    }

    setUploadingAttachment(true);
    try {
      await customerService.createContractAttachment(
        customer._id,
        contract._id,
        file
      );
      await fetchAttachments();
    } catch (message) {
      notify.error({ message });
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function deleteAttachment() {
    setDeletingAttachment(true);
    try {
      if (customer?._id && contract?._id && attachmentsToDelete?._id) {
        await customerService.deleteContractAttachment(
          customer._id,
          contract._id,
          attachmentsToDelete!._id!
        );
        setAttachmentsToDelete(null);
        fetchAttachments();
      }
    } catch (message) {
      notify.error({ message });
    } finally {
      setDeletingAttachment(false);
    }
  }

  const deleteAttachmentConfirmationDialog = useSimpleDialog({
    title: "Delete Attachment",
    message: "Are you sure you want to delete this attachment?",
    confirmButtonText: "Delete",
    onConfirm: deleteAttachment,
    icon: DeleteIcon,
  });

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  useEffect(() => {
    if (!contract) {
      fetchContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (attachmentsToDelete) {
      deleteAttachmentConfirmationDialog.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentsToDelete]);

  return (
    <>
      <PageHeader title="Contract" />
      <SubHeader subtitle="Attachments" />
      <Box className={styles.container}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={onFileSelected}
        />
        <Box className={styles.btnContainer}>
          <PrimaryButton
            text="Add Attachment"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              !contract ||
              uploadingAttachment ||
              downloadingAttachment ||
              deletingAttachment
            }
          />
        </Box>
        <Box className={styles.list}>
          {attachments.map((attachment) => (
            <Box key={attachment._id} className={styles.attachmentContainer}>
              <Box className={styles.attachment}>
                <span
                  className={styles.name}
                  onClick={() => downloadAttachment(attachment)}
                >
                  {attachment.file_name}
                </span>
                <span className={styles.date}>
                  Created:{" "}
                  {dayjs(attachment.created_at).format("YYYY-MM-DD HH:mm:ss")},
                  Updated:{" "}
                  {dayjs(attachment.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                </span>
              </Box>
              <Box className={styles.actions}>
                <IconButton
                  onClick={() => {
                    setAttachmentsToDelete(attachment);
                  }}
                  title="Delete"
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
        <RiverSpinner
          show={
            loadingAttachments || uploadingAttachment || downloadingAttachment
          }
        />
      </Box>

      {deleteAttachmentConfirmationDialog.render()}
    </>
  );
};
